import { useEffect, useState } from 'react'
import { useSetAtom, useAtomValue } from 'jotai'
import { countryState, countriesAtom } from '@repo/unicity'
import type { TCountry } from '@repo/unicity'

// Hardcoded Japan country code
const JAPAN_SHORT_CODE = 'JPN'
const JAPAN_SHORTER_CODE = 'JP'

/**
 * Hook to initialize Japan country for Spotlight app
 * - Uses countries data from /settings/countryV2/enable/all API (via countriesAtom)
 * - Finds Japan from data.ushop or data.feelgreat
 * - Sets countryState to Japan
 */
export const useJapanCountry = () => {
    const setCountry = useSetAtom(countryState)
    const countries = useAtomValue(countriesAtom)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [isSet, setIsSet] = useState(false)

    useEffect(() => {
        // Skip if already set
        if (isSet) return

        // Wait for countries to be loaded
        if (!countries?.data) {
            return
        }

        try {
            // Try to find Japan from ushop first
            let japanCountry = countries.data.ushop?.find(c => c.short === JAPAN_SHORT_CODE || c.shorter === JAPAN_SHORTER_CODE)

            // If not found in ushop, try feelgreat
            if (!japanCountry) {
                const feelgreatMatch = countries.data.feelgreat?.find(c => c.short === JAPAN_SHORT_CODE || c.shorter === JAPAN_SHORTER_CODE)
                japanCountry = feelgreatMatch as unknown as TCountry | undefined
            }

            if (japanCountry) {
                console.log('[useJapanCountry] Setting Japan country:', japanCountry.roman_name)
                setCountry(japanCountry)
                setIsSet(true)
                setError(null)
            } else {
                setError('Japan country not found in countries data')
                console.error('[useJapanCountry] Japan not found in countries data')
            }
        } catch (err) {
            console.error('[useJapanCountry] Error:', err)
            setError(err instanceof Error ? err.message : 'Failed to set Japan country')
        } finally {
            setIsLoading(false)
        }
    }, [countries, setCountry, isSet])

    return {
        isLoading: isLoading && !isSet,
        error,
        countryCode: JAPAN_SHORT_CODE
    }
}

export default useJapanCountry
